import axios from 'axios';

import { readVideoDurationSeconds } from './duration';
import type { UploadFn, UploadedFile } from './types';

export type PresignResult = {
  /** The storage URL (S3/Azure) to PUT the raw bytes to. */
  uploadUrl: string;
  /** The ref to commit to the form once the PUT succeeds. */
  file: UploadedFile;
  /** Headers the presigned URL requires on the PUT (S3 signs these — forward verbatim). */
  putHeaders?: Record<string, string>;
};

/** Extra context handed to `getPresignedUrl` so the presign request can carry it. */
export type PresignContext = { durationSeconds?: number };

/**
 * Build an UploadFn that performs the standard presign -> direct PUT flow.
 * Storage URLs are off-origin, so this uses a bare axios.put (not the API
 * interceptor client). Provider-specific concerns live in getPresignedUrl.
 *
 * Video duration is computed here (once, for every consumer) and passed to
 * `getPresignedUrl` so it can be sent as `durationSeconds`; the presigned URL then
 * requires an `x-amz-meta-duration-seconds` header, forwarded verbatim via `putHeaders`.
 */
export function createPresignedUploader(options: {
  getPresignedUrl: (file: File, ctx: PresignContext) => Promise<PresignResult>;
}): UploadFn {
  return async (file, { onProgress, signal }) => {
    const durationSeconds = await readVideoDurationSeconds(file);
    const {
      uploadUrl,
      file: stored,
      putHeaders,
    } = await options.getPresignedUrl(file, {
      durationSeconds,
    });
    await axios.put(uploadUrl, file, {
      signal,
      // `putHeaders` are the presign's requiredHeaders (incl. Content-Type); they win.
      headers: { 'Content-Type': file.type, ...putHeaders },
      onUploadProgress: (event) => {
        if (event.total) onProgress(Math.round((event.loaded / event.total) * 100));
      },
    });
    return stored;
  };
}
